import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../../../utils/authHeader";
import { Dialog, DialogClose, DialogContent } from "../Dialog";
import { useParallelUpload } from "../../../hooks/useParallelUpload";
import { useQuotaCheck } from "../../../hooks/useQuotaCheck";
import { FileUploadState, UploadPhase, UploadStatus } from "../../listings/types";
import CompletedMediaGrid from "../CompletedMediaGrid";
import UploadQueue from "../UploadQueue";
import PlanLimitReached from "../PlanLimitReached";
import CameraSettingsOverlay from "../CameraOverlay";
import ColorField from "../ColorField";
import { AlertCircle, Camera, Check, Info, X } from "lucide-react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { Button } from "../Button";
import VideoRecorderModal, { type VideoRecorderModalProps } from "../VideoRecorderModal";
import CaptioningUsageBar from "./CaptioningUsageBar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../Select";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { cn } from "../../../utils/classMerger";
import { ZAPCAP_POLICY } from "../../../constants/video-policies";
import { validateVideoProbe, VideoProbe } from "../../../utils/validateVideoProbe";
import { probeUploadedVideo, removeUploadedFile } from "../../../utils/videoProbeApi";
import { browserVideoProbe } from "../../../utils/browserVideoProbe";
import { CAPTION_POSITIONS, CaptionPosition } from "../../../types/captionPosition";
import type { CaptionStyle } from "../../../types/captionStyle";

export type { CaptionStyle } from "../../../types/captionStyle";

/**
 * Caption styles map to ZapCap templates (see zapcap-poc spec).
 * `templateId` is what the backend sends to ZapCap's render API.
 * `gif` is a local preview asset served from /public/caption-styles/.
 */
export const CAPTION_STYLES: ReadonlyArray<{
    id: CaptionStyle;
    label: string;
    description: string;
    templateId: string;
    gif: string;
}> = [
    {
        id: "clean",
        label: "Clean",
        description: "Simple bold text, no effects",
        templateId: "07ffd4b8-4e1a-4ee3-8921-d58802953bcd", // Celine
        gif: "/caption-styles/celine.gif",
    },
    {
        id: "boldPop",
        label: "Bold Pop",
        description: "Italic with color highlights",
        templateId: "e7e758de-4eb4-460f-aeca-b2801ac7f8cc", // Ella
        gif: "/caption-styles/ella.gif",
    },
    {
        id: "glow",
        label: "Glow",
        description: "Animated glow with emoji",
        templateId: "d46bb0da-cce0-4507-909d-fa8904fb8ed7", // Devin
        gif: "/caption-styles/devin.gif",
    },
    {
        id: "classic",
        label: "Classic",
        description: "Bold italic with emoji",
        templateId: "a6760d82-72c1-4190-bfdb-7d9c908732f1", // Dan
        gif: "/caption-styles/dan.gif",
    },
    {
        id: "chat",
        label: "Chat",
        description: "Speech bubble with emoji",
        templateId: "dfe027d9-bd9d-4e55-a94f-d57ed368a060", // Jordan
        gif: "/caption-styles/jordan.gif",
    },
    {
        id: "soft",
        label: "Soft",
        description: "Subtle glow, modern feel",
        templateId: "982ad276-a76f-4d80-a4e2-b8fae0038464", // Luke
        gif: "/caption-styles/luke.gif",
    },
];

export interface BrandColors {
    main: string;
    secondary: string;
    accent: string;
}

// Placeholder defaults — wire to MyBrand settings later.
export const DEFAULT_BRAND_COLORS: BrandColors = {
    main: "#193456",
    secondary: "#E9C468",
    accent: "#D5DBE1",
};

export interface BrandPalette {
    id: string;
    label: string;
    colors: BrandColors;
}

export type ColorSource =
    | { type: "brokerage"; id: string }
    | { type: "custom"; colors: BrandColors };

const CAPTION_POSITION_LABELS: Record<CaptionPosition, string> = {
    top: "Top",
    middle: "Middle",
    bottom: "Bottom",
};

const CUSTOM_OPTION_ID = "__custom__";
const CUSTOM_OPTION_LABEL = "Custom — Pick your own colors";

// Populated at runtime from GET /settings/brokerages/list — no hardcoded list needed.
export const DEFAULT_BRAND_PALETTES: BrandPalette[] = [];

const COLOR_FIELDS: ReadonlyArray<{ key: keyof BrandColors; label: string }> = [
    { key: "main", label: "First Color" },
    { key: "secondary", label: "Second Color" },
    { key: "accent", label: "Third Color" },
];

const ACCEPTED_MIME = ["video/mp4", "video/quicktime", "video/webm"];
const ACCEPT_ATTR = ACCEPTED_MIME.join(",");
// Only the size limit is enforced pre-upload. Duration, resolution, fps and
// codec are validated post-upload via /services/probe-video + ZAPCAP_POLICY.
const MAX_BYTES = ZAPCAP_POLICY.maxBytes;

interface CaptionedVideoMediaStepProps {
    videoFile: File | null;
    setVideoFile: (file: File | null) => void;
    captionStyle: CaptionStyle;
    setCaptionStyle: (style: CaptionStyle) => void;
    captionPosition: CaptionPosition;
    setCaptionPosition: (position: CaptionPosition) => void;
    palettes?: BrandPalette[];
    /** Initial color source. When a user hasn't picked yet, parent should pass the user's BE-saved preference. Falls back to the first brokerage. */
    initialColorSource?: ColorSource;
    /** Called whenever the user changes the color source. Parent should persist to the BE preference endpoint. */
    onColorSourceChange?: (source: ColorSource) => void;
    /** Called whenever the effective brand colors change — convenience for callers that only need the resolved colors. */
    onBrandColorsChange?: (colors: BrandColors) => void;
    /** Called with the GCS filename once the standard upload pipeline completes, or null when the file is removed or replaced. */
    onUploadedFilenameChange?: (filename: string | null) => void;
    /** The parent's finished upload, if any. It outlives this step: the wizard unmounts the step on Back. */
    uploadedFilename?: string | null;
    /** My Niche Video: recording is the only way in — no upload, no drag and drop. */
    recordOnly?: boolean;
    /** Shown on the recorder's teleprompter; never part of the video. */
    teleprompterScript?: string;
    /** The recorder's caption band and face guide, with its Guides toggle. */
    captionGuide?: VideoRecorderModalProps["captionGuide"];
}

/**
 * Cheap pre-upload check: MIME + byte size only.
 *
 * The authoritative validation (duration, resolution, fps, codec) happens
 * server-side after upload via /services/probe-video — see the customVideoValidator
 * passed to useParallelUpload below. These pre-checks just avoid burning
 * upload bandwidth on obviously-unsupported files.
 *
 * Error wording is kept identical to the previous pre-upload validator.
 */
const preUploadValidate = (
    file: File
): { ok: true } | { ok: false; error: string } => {
    if (!file.type.startsWith("video/")) {
        return { ok: false, error: "Only video files are accepted. Please upload a video in MP4, MOV, or WebM format." };
    }
    if (!ACCEPTED_MIME.includes(file.type)) {
        return { ok: false, error: `"${file.name}" is not a supported format. Please upload your video as MP4, MOV, or WebM.` };
    }
    if (file.size > MAX_BYTES) {
        return { ok: false, error: "Your video exceeds the 600 MB size limit. Please upload a video within the allowed size." };
    }
    return { ok: true };
};

const CaptionStyleCard: React.FC<{
    style: { id: CaptionStyle; label: string; description: string; gif: string };
    selected: boolean;
    onSelect: () => void;
}> = ({ style, selected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
            "relative flex flex-col rounded-lg overflow-hidden text-left transition-all border-2 p-0",
            selected
                ? "border-amber-500"
                : "border-neutral-200 hover:border-neutral-300"
        )}
    >
        <div className="bg-[#0F1B2D] aspect-video overflow-hidden">
            <img
                src={style.gif}
                alt={`${style.label} caption preview`}
                loading="lazy"
                className={`w-full h-full block ${
                    style.id === "clean" || style.id === "boldPop"
                        ? "object-fill"
                        : style.id === "classic" || style.id === "soft"
                            ? "object-cover object-bottom"
                            : "object-cover"
                }`}
            />
        </div>
        {selected && (
            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow">
                <Check className="w-4 h-4" />
            </span>
        )}
        <div
            className={cn(
                "px-2 py-2 transition-colors",
                selected ? "bg-amber-50" : ""
            )}
        >
            <div className="text-primary font-semibold text-sm">
                {style.label}
            </div>
            <div className="text-xs text-neutral-500">{style.description}</div>
        </div>
    </button>
);

const CaptionedVideoMediaStep: React.FC<CaptionedVideoMediaStepProps> = ({
    videoFile,
    setVideoFile,
    captionStyle,
    setCaptionStyle,
    captionPosition,
    setCaptionPosition,
    palettes = DEFAULT_BRAND_PALETTES,
    initialColorSource,
    onColorSourceChange,
    onBrandColorsChange,
    onUploadedFilenameChange,
    uploadedFilename = null,
    recordOnly = false,
    teleprompterScript,
    captionGuide,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const captionPositionRefs = useRef<Partial<Record<CaptionPosition, HTMLButtonElement | null>>>({});
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [planLimit, setPlanLimit] = useState(false);
    // Distinguishes the ZapCap-seconds limit from a generic storage limit so
    // the modal shows the right copy.
    const [planLimitType, setPlanLimitType] = useState<"storage" | "zapcap">(
        "storage"
    );
    const [showCameraSettings, setShowCameraSettings] = useState(false);
    const isMobile = useIsMobile(700);
    // Radio-group keyboard pattern: the arrows pick the neighbouring position
    // (wrapping) and take focus with it; the group is a single tab stop.
    const handleCaptionPositionKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        from: CaptionPosition
    ) => {
        const step =
            event.key === "ArrowRight" || event.key === "ArrowDown" ? 1
            : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1
            : 0;
        if (!step) return;
        event.preventDefault();
        const count = CAPTION_POSITIONS.length;
        const next = CAPTION_POSITIONS[(CAPTION_POSITIONS.indexOf(from) + step + count) % count];
        setCaptionPosition(next);
        captionPositionRefs.current[next]?.focus();
    };
    const isMobileDevice =
        typeof navigator !== "undefined" &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const { checkMultipleFilesQuota } = useQuotaCheck();

    /**
     * Runs after the file has uploaded to GCS. Probes via /services/probe-video,
     * applies ZAPCAP_POLICY, and on failure deletes the just-uploaded GCS object.
     * Returning `valid: false` makes useParallelUpload throw and mark the file FAILED;
     * the onFileError callback below surfaces the message to the user.
     */
    const zapcapVideoValidator = useCallback(
        async (
            filename: string,
            origName: string
        ): Promise<{
            id: string;
            name: string;
            valid: boolean;
            reason?: string;
            thumbnail?: string;
        }> => {
            try {
                const probe = await probeUploadedVideo(filename, origName, "asset");
                const policyResult = validateVideoProbe(probe, ZAPCAP_POLICY);
                if (!policyResult.valid) {
                    await removeUploadedFile(filename, "asset");
                    return { id: filename, name: origName, valid: false, reason: policyResult.error };
                }
                return {
                    id: filename,
                    name: origName,
                    valid: true,
                    thumbnail: probe.thumbnail ?? undefined,
                };
            } catch (err: any) {
                // Probe network/server error — also clean up to avoid orphans.
                await removeUploadedFile(filename, "asset");
                return {
                    id: filename,
                    name: origName,
                    valid: false,
                    reason: err?.response?.data?.error || err?.message || "Something went wrong.",
                };
            }
        },
        []
    );

    const parallelUpload = useParallelUpload({
        type: "assetVideo",
        customVideoValidator: zapcapVideoValidator,
        onFileError: (_fileId, error) => setValidationError(error),
    });

    // API-loaded palettes replace the hardcoded prop default.
    const [loadedPalettes, setLoadedPalettes] = useState<BrandPalette[] | null>(null);
    const effectivePalettes = loadedPalettes ?? palettes;

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const authHeader = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

                const [brokeragesRes, brandingRes] = await Promise.all([
                    axios.get<{ _id: string; name: string; zapcapColors: string[] }[]>(
                        `${backendUrl}/settings/brokerages/list`,
                        { headers: authHeader }
                    ),
                    axios.get<{
                        useCustomColorsForZapcap: boolean;
                        brokerageId: string;
                        mainColor: string;
                        secondColor: string;
                        thirdColor: string;
                    }>(
                        `${backendUrl}/settings/branding`,
                        { headers: authHeader }
                    ),
                ]);

                const mapped: BrandPalette[] = brokeragesRes.data.map((b) => ({
                    id: b._id,
                    label: b.name,
                    colors: {
                        main: b.zapcapColors[0] ?? "#000000",
                        secondary: b.zapcapColors[1] ?? "#000000",
                        accent: b.zapcapColors[2] ?? "#FFFFFF",
                    },
                }));
                setLoadedPalettes(mapped);

                const branding = brandingRes.data;
                if (branding.useCustomColorsForZapcap) {
                    setColorSourceState({
                        type: "custom",
                        colors: {
                            main: branding.mainColor,
                            secondary: branding.secondColor,
                            accent: branding.thirdColor,
                        },
                    });
                } else {
                    setColorSourceState({ type: "brokerage", id: branding.brokerageId });
                }
            } catch {
                // Silently fall back to the hardcoded defaults already in state.
            }
        };
        fetchInitialData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Watch for a completed upload and propagate the GCS filename to the parent.
    useEffect(() => {
        const completed = parallelUpload.queueState.files.find(
            (f) => f.status === UploadStatus.COMPLETED && f.filename
        );
        if (completed?.filename) {
            onUploadedFilenameChange?.(completed.filename);
        }
    }, [parallelUpload.queueState.files, onUploadedFilenameChange]);

    const handleRemoveUploadedFile = useCallback(
        (fileId: string) => {
            parallelUpload.removeFile(fileId);
            setVideoFile(null);
            onUploadedFilenameChange?.(null);
        },
        [parallelUpload, setVideoFile, onUploadedFilenameChange]
    );

    const fallbackSource: ColorSource = {
        type: "brokerage",
        id: effectivePalettes[0]?.id ?? "default",
    };
    const [colorSource, setColorSourceState] = useState<ColorSource>(
        initialColorSource ?? fallbackSource
    );

    const resolveBrokerage = (id: string): BrandPalette =>
        effectivePalettes.find((p) => p.id === id) ??
        effectivePalettes[0] ??
        { id: "", label: "", colors: DEFAULT_BRAND_COLORS };

    const isCustom = colorSource.type === "custom";
    const effectiveColors: BrandColors = isCustom
        ? colorSource.colors
        : resolveBrokerage(colorSource.id).colors;
    const sourceLabel: string = isCustom
        ? CUSTOM_OPTION_LABEL
        : resolveBrokerage(colorSource.id).label;

    const updateSource = (next: ColorSource) => {
        setColorSourceState(next);
        onColorSourceChange?.(next);
    };

    // Notify parent whenever colorSource changes, including on initial load from the
    // fetchInitialData effect (which calls setColorSourceState directly, not updateSource).
    useEffect(() => {
        onColorSourceChange?.(colorSource);
    }, [colorSource, onColorSourceChange]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        onBrandColorsChange?.(effectiveColors);
    }, [effectiveColors, onBrandColorsChange]);

    const handleSourceDropdownChange = (id: string) => {
        if (id === CUSTOM_OPTION_ID) {
            updateSource({ type: "custom", colors: { ...effectiveColors } });
        } else {
            updateSource({ type: "brokerage", id });
        }
    };

    const updateColor = (key: keyof BrandColors, value: string) => {
        updateSource({
            type: "custom",
            colors: { ...effectiveColors, [key]: value },
        });
    };

    const handleFile = async (file: File) => {
        const pre = preUploadValidate(file);
        if (!pre.ok) {
            setValidationError(pre.error);
            return;
        }

        // Probe the file in the browser FIRST so we know its duration before
        // the quota check. Duration is what /check-video-limit needs to apply
        // the ZapCap hard-cap (used + new > zapcapHardCap) — without it, only
        // the soft cap is enforced and an over-limit video can sneak through.
        // Falls back silently when the browser can't decode the metadata; the
        // BE probe still catches the rest after upload.
        let videoDurationS: number | undefined;
        try {
            const meta = await browserVideoProbe(file);
            videoDurationS = meta.duration;
            const partialProbe: VideoProbe = {
                success: true,
                name: file.name,
                container: file.type.split("/")[1]?.toLowerCase() ?? null,
                codec: null,
                width: meta.width,
                height: meta.height,
                fps: null,
                duration: meta.duration,
                sizeBytes: file.size,
                hasAudio: null,
                audioCodec: null,
                probeError: null,
                thumbnail: null,
            };
            const policyResult = validateVideoProbe(partialProbe, ZAPCAP_POLICY);
            if (!policyResult.valid) {
                setValidationError(policyResult.error ?? "Something went wrong.");
                return;
            }
        } catch {
            // Browser couldn't decode metadata (rare container variant, codec
            // not supported by <video>, etc.) — proceed with duration unknown;
            // BE will enforce the hard cap once the post-upload ffprobe runs.
        }

        // Quota + ZapCap-seconds check. Passing the probed duration lets the BE
        // reject `used + duration > hardCap` cases pre-upload.
        const quotaCheck = await checkMultipleFilesQuota(
            [file],
            "assetVideo",
            "Caption Video",
            videoDurationS,
        );
        if (!quotaCheck.canUpload) {
            setPlanLimitType(quotaCheck.videoLimitOk ? "storage" : "zapcap");
            setPlanLimit(true);
            return;
        }

        // Captioned video allows only one video at a time — clear any existing entry first.
        if (parallelUpload.queueState.files.length > 0) {
            parallelUpload.cancelAllUploads();
            setVideoFile(null);
        }
        // Always drop the parent's filename, queue or not: it outlives this step
        // (the wizard unmounts it on Back), so Next must wait for the new upload
        // rather than submit the previous one.
        onUploadedFilenameChange?.(null);

        // Hand off to the upload pipeline. The customVideoValidator above runs
        // /services/probe-video + ZAPCAP_POLICY after the GCS upload completes,
        // and deletes the GCS object on failure.
        setVideoFile(file);
        parallelUpload.addFiles([file]);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = "";
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleClickUpload = () => fileInputRef.current?.click();
    // Back and forth through the wizard: the finished upload is still what Next
    // posts, though this remount's queue is empty.
    const holdsUpload = !!videoFile && !!uploadedFilename && parallelUpload.queueState.files.length === 0;
    // The queue's card went with the unmount, so show the held take from the file the wizard
    // still has, as the same completed-media card; Remove clears it for a fresh take.
    // Made and released in one effect: dev's StrictMode runs the cleanup once mid-mount.
    const [heldPreview, setHeldPreview] = useState<string | null>(null);
    useEffect(() => {
        if (!holdsUpload || !videoFile) {
            setHeldPreview(null);
            return;
        }
        const url = URL.createObjectURL(videoFile);
        setHeldPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [holdsUpload, videoFile]);
    const heldTake: FileUploadState | null = holdsUpload && videoFile && heldPreview !== null
        ? {
            id: "held-take", file: videoFile, preview: heldPreview, status: UploadStatus.COMPLETED,
            progress: 100, phase: UploadPhase.PERSISTENCE, retryCount: 0, filename: uploadedFilename ?? undefined,
        }
        : null;
    const removeHeldTake = (): void => {
        setVideoFile(null);
        onUploadedFilenameChange?.(null);
    };

    // Both steps show this under the Record Video / Select File buttons: the recorder's caption band follows
    // the pick, so it is chosen before recording.
    const captionPositionGroup = (
        <div role="radiogroup" aria-label="Caption position" className="inline-flex rounded-md border border-neutral-300 divide-x divide-neutral-300 overflow-hidden">
            {CAPTION_POSITIONS.map((p) => (
                <button key={p} type="button" role="radio" aria-checked={captionPosition === p}
                    ref={(el) => { captionPositionRefs.current[p] = el; }}
                    tabIndex={captionPosition === p ? 0 : -1}
                    onClick={() => setCaptionPosition(p)}
                    onKeyDown={(e) => handleCaptionPositionKeyDown(e, p)}
                    // Inset ring: the group's overflow-hidden clips an outer one. Gold on the navy checked segment, navy on white.
                    className={cn("px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset", captionPosition === p ? "bg-primary text-white focus-visible:ring-secondary" : "bg-white text-primary hover:bg-neutral-100 focus-visible:ring-primary")}>
                    {CAPTION_POSITION_LABELS[p]}
                </button>
            ))}
        </div>
    );
    const captionPositionHint = (
        <p className="text-xs text-neutral-500 mt-2">Where the captions sit on the video. Bottom is the usual choice.</p>
    );
    const captionPositionRow = (
        <div className={cn("flex flex-col items-center text-center", recordOnly ? "mt-2" : "mt-3")}>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                <span className="text-sm font-semibold text-primary">Caption Position:</span>
                {captionPositionGroup}
            </div>
            {captionPositionHint}
        </div>
    );

    return (
        <div className="flex flex-col gap-6">
            {/* Validation error dialog — same pattern as DuplicateYouTubeVideoAlert */}
            <Dialog open={!!validationError} onOpenChange={(o) => { if (!o) setValidationError(null); }}>
                <DialogContent className="max-w-sm p-6 flex flex-col items-center gap-4 text-center bg-white">
                    <DialogClose className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                    </DialogClose>
                    <AlertCircle className="h-10 w-10 text-yellow-500" />
                    <p className="text-sm text-gray-800">{validationError}</p>
                    <button
                        type="button"
                        className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90"
                        onClick={() => setValidationError(null)}
                    >
                        Close
                    </button>
                </DialogContent>
            </Dialog>

            {planLimit && (
                <PlanLimitReached
                    open={planLimit}
                    onClose={() => setPlanLimit(false)}
                    type={planLimitType}
                />
            )}
            <h2 className="text-xl font-bold text-primary text-center">
                {recordOnly ? "Record Your Video" : "Select Media"}
            </h2>

            <div className="border p-4 rounded-md">
                <div className="mb-3 flex flex-col gap-2 min-[640px]:flex-row min-[640px]:items-center min-[640px]:justify-between min-[640px]:gap-4">
                    <h4 className="text-base font-semibold text-primary whitespace-nowrap">
                        {recordOnly ? "Record New Video" : "Upload New Media"}
                    </h4>
                    <CaptioningUsageBar />
                </div>

                <div className="bg-gray-100 p-3 rounded-md mb-4 flex flex-row items-start justify-between gap-2">
                    {recordOnly ? (
                        <p className="text-sm text-primary">
                            {!isMobileDevice && <>For best results, record on your iPhone or Android phone. </>}
                            Your script scrolls on screen while you record. It is not part of the video.
                        </p>
                    ) : (
                        <p className="text-sm text-red-500">
                            <strong className="text-primary">Note: </strong>
                            {!isMobileDevice && (
                                <>
                                    For best results, please record from your iPhone or Android device.{" "}
                                </>
                            )}
                            Use{" "}
                            <strong className="text-red-500">Record Video</strong>{" "}
                            below to capture new footage. The camera option inside{" "}
                            <strong className="text-red-500">Select File</strong>{" "}
                            records at low resolution and uploads will fail.
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowCameraSettings(true)}
                        className="flex-shrink-0 text-primary hover:text-black"
                        aria-label="Camera Settings Guide"
                    >
                        <Info className="h-5 w-5" />
                    </button>
                </div>

                {showCameraSettings && (
                    <div className="relative mb-4">
                        <CameraSettingsOverlay
                            onClose={() => setShowCameraSettings(false)}
                        />
                    </div>
                )}

                {!recordOnly && holdsUpload && (
                    <p className="mb-3 text-sm text-primary text-center">
                        Your video is ready - record or upload again to replace it.
                    </p>
                )}
                {recordOnly ? (
                    <div className="flex flex-col items-center gap-2 py-6">
                        {holdsUpload && (
                            <p className="text-sm text-primary text-center">
                                Your recording is ready - record again to replace it.
                            </p>
                        )}
                        <Button
                            id="record-video-button"
                            type="button"
                            size="sm"
                            className="flex items-center gap-1.5 w-36"
                            onClick={() => setIsRecording(true)}
                        >
                            <Camera className="h-4 w-4" />
                            Record Video
                        </Button>
                        {captionPositionRow}
                    </div>
                ) : (
                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={handleClickUpload}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleClickUpload();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Drag and drop area for videos"
                        className="border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                    >
                        <FaCloudUploadAlt
                            className="mx-auto text-secondary mb-4"
                            size={isMobile ? 36 : 48}
                        />
                        <p
                            className={cn(
                                "text-primary mb-2",
                                isMobile ? "text-base" : "text-lg"
                            )}
                        >
                            Drag and drop your videos here
                        </p>
                        <p className={cn("text-primary text-sm", isMobile && "text-xs")}>
                            or
                        </p>
                        <div className="flex flex-col items-center gap-2 mt-2">
                            <Button
                                id="record-video-button"
                                type="button"
                                size="sm"
                                className="flex items-center gap-1.5 w-36"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsRecording(true);
                                }}
                            >
                                <Camera className="h-4 w-4" />
                                Record Video
                            </Button>
                            <Button
                                id="select-files-button"
                                type="button"
                                size="sm"
                                className="w-36"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickUpload();
                                }}
                            >
                                Select File
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPT_ATTR}
                                className="hidden"
                                onChange={handleFileInput}
                            />
                        </div>
                    </div>
                )}

                {!recordOnly && captionPositionRow}

                <p className="text-sm text-neutral-500 mt-3">
                    {recordOnly ? "Max duration: 2 minutes" : "Maximum video size: 600 MB · Max duration: 2 minutes"}
                </p>

                {!recordOnly && (
                    <div className="bg-gray-100 p-3 rounded-md mt-3">
                        <p className="text-sm text-primary font-semibold mb-1">Note:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-sm text-neutral-700">
                            <li>Video files only — images will be rejected.</li>
                            <li>HD resolution required.</li>
                            <li>Accepted formats: MP4, MOV, WebM.</li>
                        </ul>
                    </div>
                )}

            </div>

            {heldTake && (
                <CompletedMediaGrid completedFiles={[heldTake]} failedFiles={[]} cancelledFiles={[]} onRemove={removeHeldTake} onRetry={() => undefined} />
            )}

            {parallelUpload.queueState.files.length > 0 && (
                <UploadQueue
                    queueState={parallelUpload.queueState}
                    onRemoveFile={handleRemoveUploadedFile}
                    onRetryFile={parallelUpload.retryFile}
                    onCancelFile={parallelUpload.cancelFile}
                    onClearCompleted={parallelUpload.clearCompleted}
                />
            )}

            <div className="border-t border-neutral-200 pt-5 space-y-3">
                <div>
                    <h4 className="text-base font-semibold text-primary">
                        Brand Colors
                    </h4>
                    <p className="text-sm text-neutral-500">
                        Colors are set to your brokerage brand. You can customize below.
                    </p>
                </div>

                <Select
                    value={isCustom ? CUSTOM_OPTION_ID : colorSource.id}
                    onValueChange={handleSourceDropdownChange}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select brokerage" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={CUSTOM_OPTION_ID}>
                            {CUSTOM_OPTION_LABEL}
                        </SelectItem>
                        {effectivePalettes.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                                {p.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <div className="pt-2">
                    <div className="flex items-baseline justify-between mb-3">
                        <h5 className="text-xs font-bold text-neutral-500 tracking-wider">
                            {isCustom ? "EDIT COLORS" : "COLOR PREVIEW"}
                        </h5>
                        {isCustom && (
                            <span className="text-xs text-neutral-500">
                                Click any color to edit
                            </span>
                        )}
                    </div>
                    <div className={cn("grid gap-2", isMobile ? "grid-cols-2" : "grid-cols-3")}>
                        {COLOR_FIELDS.map(({ key, label }) => (
                            <ColorField
                                key={key}
                                label={label}
                                color={effectiveColors[key]}
                                disabled={!isCustom}
                                onChange={(value) => updateColor(key, value)}
                            />
                        ))}
                    </div>
                    <p className="text-xs text-neutral-500 mt-3">
                        {isCustom
                            ? "Using custom colors — selecting a brokerage above will discard them"
                            : `Using ${sourceLabel} brand palette`}
                    </p>
                </div>
            </div>

            <div className="border-t border-neutral-200 pt-5">
                <h4 className="text-xs font-bold text-neutral-500 tracking-wider mb-3">
                    CAPTION STYLE
                </h4>
                <div className={`grid ${isMobile ? "grid-cols-2" : "grid-cols-3"} gap-4`}>
                    {CAPTION_STYLES.map((style) => (
                        <CaptionStyleCard
                            key={style.id}
                            style={style}
                            selected={captionStyle === style.id}
                            onSelect={() => setCaptionStyle(style.id)}
                        />
                    ))}
                </div>
                <div className="bg-gray-100 p-3 rounded-md mt-3">
                    <p className="text-sm text-primary">
                        <strong>Note: </strong>Previews show default colors —
                        your selected colors are applied automatically.
                    </p>
                </div>
            </div>

            {isRecording && (
                <VideoRecorderModal
                    onClose={() => setIsRecording(false)}
                    onRecordingComplete={(file) => {
                        setIsRecording(false);
                        handleFile(file);
                    }}
                    enforceAspectRatio
                    portraitOnly
                    teleprompterScript={teleprompterScript}
                    captionGuide={captionGuide}
                />
            )}
        </div>
    );
};

export default CaptionedVideoMediaStep;
